import { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { testimonials } from '../data/testimonials';
import { Button } from './ui/button';

export const Testimonials = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('testimonials');
    if (element) observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, []);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    let interval;
    if (isAutoPlay && testimonials.length > 1) {
      interval = setInterval(nextTestimonial, 5000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlay]);

  // Render nothing rather than an empty carousel when there are no published
  // testimonials. This sits below every hook so the hook order stays stable.
  // See src/data/testimonials.js for why the list is currently empty.
  if (testimonials.length === 0) {
    return null;
  }

  return (
    <section id="testimonials" className="py-24 bg-white relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-blue/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2 animate-pulse"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-brand-teal/5 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2 animate-pulse delay-700"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div
          className={`text-center mb-16 transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
        >
          <div className="inline-block px-5 py-2 bg-brand-blue/10 text-brand-navy rounded-full text-sm font-bold mb-6 tracking-wide uppercase hover:bg-brand-blue hover:text-white transition-colors duration-300">
            Success Stories
          </div>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-gray-900 mb-6 leading-tight">
            What Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-brand-teal">Patients Say</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Real experiences from international patients who trusted us with their healthcare journey.
          </p>
        </div>

        <div
          className="max-w-5xl mx-auto"
          onMouseEnter={() => setIsAutoPlay(false)}
          onMouseLeave={() => setIsAutoPlay(true)}
        >
          <div className="relative">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className={`transition-all duration-700 ease-in-out absolute inset-0 w-full ${index === currentIndex
                    ? 'opacity-100 translate-x-0 relative z-20'
                    : 'opacity-0 translate-x-8 absolute z-0 pointer-events-none'
                  }`}
              >
                <div className="bg-white rounded-[3rem] p-8 lg:p-12 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100 relative group hover:shadow-[0_30px_90px_-20px_rgba(31,95,139,0.15)] transition-shadow duration-500">
                  {/* Big Quote Icon */}
                  <div className="absolute top-8 right-8 lg:top-12 lg:right-12 text-brand-blue/10 group-hover:text-brand-blue/20 transition-colors duration-500">
                    <Quote className="w-24 h-24 rotate-12" />
                  </div>

                  <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-14 relative z-10">
                    {/* Image Section */}
                    <div className="flex-shrink-0 relative group-hover:scale-105 transition-transform duration-500">
                      <div className="relative w-40 h-40 lg:w-48 lg:h-48">
                        <div className="absolute inset-0 bg-brand-blue/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        <img loading="lazy" decoding="async"
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-full h-full rounded-full object-cover shadow-xl border-4 border-white relative z-10"
                        />
                        <div className="absolute bottom-2 right-2 bg-brand-teal text-white p-2 rounded-full shadow-lg z-20">
                          <Quote className="w-4 h-4 fill-current" />
                        </div>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="flex-1 text-center lg:text-left">
                      <div className="flex items-center justify-center lg:justify-start gap-1 mb-6">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400 drop-shadow-sm animate-[spin_3s_ease-in-out_infinite]" style={{ animationDelay: `${i * 100}ms` }} />
                        ))}
                      </div>

                      <p className="text-xl lg:text-2xl text-gray-700 italic mb-8 leading-relaxed font-light">
                        "{testimonial.comment}"
                      </p>

                      <div className="border-l-4 border-brand-blue pl-6 ml-4 lg:ml-0">
                        <div className="text-2xl font-bold text-brand-navy mb-1 group-hover:text-brand-blue transition-colors">
                          {testimonial.name}
                        </div>
                        <div className="flex flex-col lg:flex-row lg:items-center gap-2 text-gray-500 text-sm font-medium tracking-wide uppercase">
                          <span className="text-brand-teal">{testimonial.country}</span>
                          <span className="hidden lg:inline text-gray-300">•</span>
                          <span>{testimonial.treatment}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Navigation Controls */}
            <div className={`flex items-center justify-center gap-6 mt-16 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <Button
                variant="outline"
                size="icon"
                onClick={prevTestimonial}
                className="w-14 h-14 rounded-full border-2 border-gray-200 text-gray-600 hover:border-brand-blue hover:bg-brand-blue hover:text-white transition-all duration-300 hover:scale-110 shadow-sm"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>

              <div className="flex gap-3 bg-gray-100/50 px-4 py-2 rounded-full backdrop-blur-sm">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`h-2.5 rounded-full transition-all duration-500 ease-out ${index === currentIndex
                        ? 'bg-gradient-to-r from-brand-blue to-brand-teal w-12 shadow-md'
                        : 'bg-gray-300 w-2.5 hover:bg-brand-blue/50'
                      }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={nextTestimonial}
                className="w-14 h-14 rounded-full border-2 border-gray-200 text-gray-600 hover:border-brand-blue hover:bg-brand-blue hover:text-white transition-all duration-300 hover:scale-110 shadow-sm"
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};